/**
 * What happened when this device tried to redeem its link.
 *
 * The surveyor taps a link in a text message. Four things can happen, and the
 * difference between them is the whole point of having a screen for it:
 *
 *   linked      — `/api/me` answered. The device knows who it is; get out of the way.
 *   rejected    — the server refused the token. Almost always a deactivation, and
 *                 the one case where the stored token must be thrown away, because
 *                 keeping it would make every later request fail silently.
 *   offline     — the request never landed. The token is FINE and stays. If the
 *                 device already knows who it is, this is not even a problem.
 *   unlinked    — this device holds no token at all.
 *
 * `unlinked` is TWO screens since D-019, and which one depends on whether the
 * app was launched from the Home Screen icon. iOS gives an installed web app a
 * storage container separate from Safari's, so linking the wrong one is both
 * possible and invisible: the installed app has to be offered the pairing path,
 * and a browser tab has to be told to install FIRST. See `AccessOffer`.
 *
 * Kept out of the component so the classification is unit-testable, and so the
 * wording is in one place rather than scattered through JSX.
 */

import type { Identity } from './identity';

export type AccessPhase = 'checking' | 'linked' | 'rejected' | 'offline' | 'unlinked';

/**
 * What this screen offers a device that cannot work yet (D-019).
 *
 * Exactly one of the three, never two, because the governing principle is one
 * obvious next action:
 *
 *   'pair'          the installed app, unlinked. Scan or type a setup code —
 *                   the thing that was impossible before D-019.
 *   'install-first' a browser tab, unlinked. Install the icon FIRST, then link
 *                   from inside it, because work saved in the browser will not
 *                   be in the installed app. Offering pairing here would link
 *                   the container the surveyor is about to abandon.
 *   'none'          nothing to offer. A device that is linked, or one whose
 *                   token was refused, where pairing is not the answer.
 */
export type AccessOffer = 'pair' | 'install-first' | 'none';

export interface AccessView {
  phase: AccessPhase;
  headline: string;
  detail: string;
  /** True when the surveyor can get on with their work from here. */
  canProceed: boolean;
  /** The one thing this screen offers an unusable device. */
  offer: AccessOffer;
}

/**
 * A rejection, distinguished from "the office is unreachable".
 *
 * 401 and 403 mean the server looked at the token and said no. `getSurveyorByToken`
 * matches on `active = 1 AND revoked_at IS NULL`, so a deactivated surveyor is
 * indistinguishable from an unknown one BY DESIGN — which is why the message
 * below names the recoverable cause rather than guessing at the technical one.
 *
 * Anything else — a thrown fetch, a 500, a 502 from a proxy — is the network,
 * and must never cost the surveyor their stored token.
 */
export function isRejection(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 401 || status === 403;
}

/**
 * The exact sentence the brief asks for, for the exact case it asks about. It
 * names the two people who can fix it and says nothing about tokens, sessions
 * or status codes, because none of those are actionable in a machine room.
 */
export const REJECTED_HEADLINE = 'This link is no longer active.';
export const REJECTED_DETAIL = 'Ask Chip or Tim for a new one.';

export function accessView(input: {
  phase: AccessPhase;
  identity: Identity | null;
  /**
   * Is this the app launched from the Home Screen icon, rather than a browser
   * tab? Only consulted in the `unlinked` case, and it changes the whole
   * screen there — see `AccessOffer`. Defaults to false, which is the safe
   * default: the install-first advice is harmless to somebody who has already
   * installed, and withdrawing it from somebody who has not is the mistake
   * that cost a day's work.
   */
  standalone?: boolean;
}): AccessView {
  switch (input.phase) {
    case 'checking':
      return {
        phase: 'checking',
        headline: 'Setting up this device…',
        detail: 'One moment.',
        canProceed: false,
        offer: 'none',
      };

    case 'linked':
      return {
        phase: 'linked',
        headline: input.identity ? `You’re set, ${firstName(input.identity.name)}.` : 'You’re set.',
        detail:
          'This device is linked. It keeps working with no signal — everything you capture ' +
          'saves here and uploads on its own.',
        canProceed: true,
        // A working device is never nagged about installing or pairing.
        offer: 'none',
      };

    case 'rejected':
      return {
        phase: 'rejected',
        headline: REJECTED_HEADLINE,
        detail: REJECTED_DETAIL,
        canProceed: false,
        // Not 'pair'. A refused token is a deactivated surveyor far more often
        // than it is a setup problem, and the office has to act first — a code
        // printed for a deactivated surveyor is refused too. Offering a pairing
        // form here would be an action that cannot work.
        offer: 'none',
      };

    case 'offline':
      // The token is stored either way. With a cached identity there is nothing
      // wrong at all, so the copy must not read like a failure.
      return input.identity
        ? {
            phase: 'offline',
            headline: `You’re set, ${firstName(input.identity.name)}.`,
            detail:
              'No signal right now, which is fine — this device is linked and everything ' +
              'saves here. It uploads when you get a bar.',
            canProceed: true,
            offer: 'none',
          }
        : {
            phase: 'offline',
            headline: 'Can’t reach the office yet.',
            detail:
              'Your link is saved on this device. Open the app again where you have a bar of ' +
              'signal and it will finish setting up.',
            canProceed: false,
            offer: 'none',
          };

    case 'unlinked':
      /*
       * The case D-019 rewrote, and the reason it had to be rewritten.
       *
       * The old copy said "open the link Chip or Tim texted you". SMS has been
       * parked indefinitely since the A2P denial (D-010), so that sentence sent
       * a surveyor away to wait for a text that will never arrive — and on iOS,
       * even if it had arrived, the link would have opened in Safari and linked
       * the wrong storage container.
       */
      return input.standalone === true
        ? {
            phase: 'unlinked',
            headline: 'Let’s set this phone up.',
            detail:
              'Stephanie has a setup code on the office screen. Point this phone at it, or ' +
              'type the letters underneath it.',
            canProceed: false,
            offer: 'pair',
          }
        : {
            phase: 'unlinked',
            headline: 'Add UpSurv to your Home Screen first.',
            detail:
              'Anything you do in this browser window will NOT be in the app once you add ' +
              'it — the phone keeps them apart. So add the icon now, open the app from it, ' +
              'and set yourself up in there.',
            canProceed: false,
            offer: 'install-first',
          };
  }
}

/** "Dave Reyes" → "Dave". A greeting, not a record. */
export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}
